/* ============================================================
   PRODUCT PERSONALIZER — POSTER MODE
   ------------------------------------------------------------
   Un solo lado, una sola foto. El cliente sube UNA imagen, la
   acomoda dentro de un marco con proporción fija (default 50x90
   vertical) usando 4 controles: horizontal, vertical, zoom y giro.
   La imagen ORIGINAL se sube en alta resolución (binario crudo,
   sin recomprimir) a Firebase Storage para imprimir el poster.
   Se guarda también una vista previa (cómo quedó encuadrada) para
   que producción reproduzca el encuadre lo más fiel posible.
   ============================================================ */
(function () {
  'use strict';

  // JPEG no tiene transparencia: lo transparente se vuelve NEGRO al exportar. Antes de
  // exportar se calca el canvas sobre blanco (si ya era opaco, no cambia nada).
  // Bug real 05/08/2026: los fondos con transparencia del Tag de Mascota se guardaban oscuros.
  function __pzSobreBlanco(cv) {
    var c = document.createElement('canvas');
    c.width = cv.width; c.height = cv.height;
    var x = c.getContext('2d');
    x.fillStyle = '#ffffff';
    x.fillRect(0, 0, c.width, c.height);
    x.drawImage(cv, 0, 0);
    return c;
  }


  var ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
  var MAX_BYTES = 24 * 1024 * 1024; // tope real de la Function

  function PosterPersonalizer(el) {
    this.el = el;
    try {
      this.config = JSON.parse(el.dataset.pzdConfig || '{}');
    } catch (e) {
      this.config = {};
    }

    this.state = {
      photoFile: null,
      photoUrl: null,
      _photoImg: null,
      transform: { x: 0, y: 0, scale: 1, rotation: 0 },
      originalUrl: null,   // URL del archivo original en Storage
      previewUrl: null,    // URL de la vista previa compuesta
      uploading: false,
      uploadDone: false
    };

    this._frameImg = null;
    this._previewTimer = null;
    this._dragging = false;

    this._init();
  }

  PosterPersonalizer.prototype._init = function () {
    this.canvas = this.el.querySelector('[data-pzp-canvas]');
    this.ctx = this.canvas ? this.canvas.getContext('2d') : null;

    this._applyStyles();
    this._setupCanvasSize();
    this._loadFrameImage();
    this._loadPlaceholderImage();
    this._drawCanvas();
    this._updateLabels();

    this.productForm = this._findProductForm();
    if (this.productForm) {
      this._injectHiddenInputs();
      this._interceptFormSubmit();
    }
    this._interceptReelAsItButton();
    this._interceptFetchAddToCart();
    this._interceptXHRAddToCart();

    this._bindEvents();
    this._updateControlsVisibility();
    this._updateStatus();
  };

  // ----------------------------------------------------------
  // LABELS — escribir los textos editables del admin
  // ----------------------------------------------------------
  PosterPersonalizer.prototype._updateLabels = function () {
    var c = this.config;
    var set = function (sel, text) {
      var el = this.el.querySelector(sel);
      if (el && text !== undefined && text !== null) el.textContent = text;
    }.bind(this);

    if (c.previewLabel) set('[data-pzp-preview-label]', c.previewLabel);
    if (c.previewHint !== undefined) {
      var hintEl = this.el.querySelector('[data-pzp-preview-hint]');
      if (hintEl) hintEl.textContent = c.previewHint ? '(' + c.previewHint + ')' : '';
    }
    if (c.uploadAreaText) set('[data-pzp-upload-area-text]', c.uploadAreaText);
    if (c.hint) set('[data-pzp-upload-hint]', c.hint);
    if (c.controlsHint) set('[data-pzp-context-hint]', c.controlsHint);
    if (c.previewBtnLabel) set('[data-pzp-preview-btn-label]', c.previewBtnLabel);
  };

  // ----------------------------------------------------------
  // STATUS — Pendiente / Listo (cabecera de vista previa)
  // ----------------------------------------------------------
  PosterPersonalizer.prototype._updateStatus = function () {
    var statusEl = this.el.querySelector('[data-pzp-status]');
    if (!statusEl) return;
    var issue = null;
    if (!this.state.photoFile || !this.state._photoImg) {
      issue = this.config.statusMissingPhoto || 'Subí tu foto';
    } else if (this.state.uploading) {
      issue = this.config.statusUploading || 'Subiendo tu foto...';
    } else if (!this.state.originalUrl) {
      issue = this.config.statusFinishing || 'Terminando de procesar...';
    }
    statusEl.classList.add('is-visible');
    statusEl.classList.remove('is-pending', 'is-ready');
    if (issue) {
      statusEl.classList.add('is-pending');
      statusEl.textContent = 'Pendiente — ' + issue;
    } else {
      statusEl.classList.add('is-ready');
      statusEl.textContent = '✓ ' + (this.config.statusReady || 'Listo — podés agregar al carrito');
    }
  };

  // ----------------------------------------------------------
  // ESTILOS (variables CSS desde el admin)
  // ----------------------------------------------------------
  PosterPersonalizer.prototype._applyStyles = function () {
    var s = this.config.styles || {};
    if (s.accentColor) this.el.style.setProperty('--pzp-accent', s.accentColor);
    if (s.labelSize) this.el.style.setProperty('--pzp-label-size', s.labelSize);
    // Negrita de etiquetas (default ON: 700; OFF: 500)
    this.el.style.setProperty('--pzp-label-weight', s.labelBold === false ? '500' : '700');
  };

  // ----------------------------------------------------------
  // CANVAS — proporción fija (default 50x90 vertical)
  // ----------------------------------------------------------
  PosterPersonalizer.prototype._posterRatio = function () {
    var wCm = parseFloat(this.config.posterWidthCm) || 50;
    var hCm = parseFloat(this.config.posterHeightCm) || 90;
    return wCm / hCm; // ancho/alto
  };

  PosterPersonalizer.prototype._setupCanvasSize = function () {
    if (!this.canvas) return;
    var ratio = this._posterRatio();
    // Resolución interna del canvas (solo display; lo que se imprime es el original)
    var baseW = 720;
    var baseH = Math.round(baseW / ratio);
    this.canvas.width = baseW;
    this.canvas.height = baseH;
    // CSS aspect-ratio para que el contenedor respete la proporción
    this.canvas.style.aspectRatio = ratio.toFixed(4);
  };

  PosterPersonalizer.prototype._loadFrameImage = function () {
    var url = this.config.frameImage;
    if (!url) { this._hideLoading(); return; }
    var self = this;
    this._showLoading();
    var img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = function () {
      self._frameImg = img;
      // El canvas adopta la proporción REAL del marco (así no se estira el mockup)
      if (img.naturalWidth > 0 && img.naturalHeight > 0) {
        var fr = img.naturalWidth / img.naturalHeight;
        var baseW = 720;
        self.canvas.width = baseW;
        self.canvas.height = Math.round(baseW / fr);
        self.canvas.style.aspectRatio = fr.toFixed(4);
      }
      self._drawCanvas();
      self._hideLoading();
    };
    img.onerror = function () { self._hideLoading(); };
    img.src = url;
  };

  // ----------------------------------------------------------
  // LOADING SPINNER (mientras carga el marco)
  // ----------------------------------------------------------
  PosterPersonalizer.prototype._showLoading = function () {
    var el = this.el.querySelector('[data-pzp-loading]');
    if (el) el.classList.remove('is-hidden');
  };
  PosterPersonalizer.prototype._hideLoading = function () {
    var el = this.el.querySelector('[data-pzp-loading]');
    if (el) el.classList.add('is-hidden');
  };

  // ----------------------------------------------------------
  // STATUS INLINE DE LA FOTO
  // (debajo de los botones Cambiar foto / Centrar)
  // ----------------------------------------------------------
  PosterPersonalizer.prototype._setPhotoStatus = function (type, text) {
    var el = this.el.querySelector('[data-pzp-photo-status]');
    if (!el) return;
    el.classList.remove('is-visible', 'is-uploading', 'is-done', 'is-error');
    if (!type || !text) { el.textContent = ''; return; }
    el.classList.add('is-visible', 'is-' + type);
    el.textContent = text;
  };

  // Área (hueco) donde va el diseño. Por defecto, casi todo el canvas.
  PosterPersonalizer.prototype._getDesignArea = function () {
    var a = this.config.designArea;
    if (a && typeof a.x === 'number') return a;
    return { x: 0.0, y: 0.0, width: 1.0, height: 1.0 };
  };

  PosterPersonalizer.prototype._drawCanvas = function () {
    if (!this.ctx) return;
    var ctx = this.ctx;
    var w = this.canvas.width;
    var h = this.canvas.height;

    ctx.clearRect(0, 0, w, h);
    // Fondo del poster (blanco)
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, w, h);

    var area = this._getDesignArea();
    var areaX = w * area.x, areaY = h * area.y;
    var areaW = w * area.width, areaH = h * area.height;

    var img = this.state._photoImg;
    if (img && img.complete && img.naturalWidth > 0) {
      // Foto del cliente, recortada al hueco del diseño, con su transform
      var t = this.state.transform;
      var coverScale = Math.max(areaW / img.naturalWidth, areaH / img.naturalHeight) * (t.scale || 1);
      var drawW = img.naturalWidth * coverScale;
      var drawH = img.naturalHeight * coverScale;
      var cx = areaX + areaW / 2 + (t.x || 0) * areaW;
      var cy = areaY + areaH / 2 + (t.y || 0) * areaH;

      ctx.save();
      ctx.beginPath();
      ctx.rect(areaX, areaY, areaW, areaH);
      ctx.clip();
      ctx.translate(cx, cy);
      ctx.rotate((t.rotation || 0) * Math.PI / 180);
      ctx.drawImage(img, -drawW / 2, -drawH / 2, drawW, drawH);
      ctx.restore();
    } else {
      // Placeholder: fondo + texto "TU DISEÑO IRÁ AQUÍ" dentro del hueco
      this._drawPlaceholder(areaX, areaY, areaW, areaH);
    }

    // Marco/mockup opcional encima (el hueco transparente deja ver lo de atrás)
    if (this._frameImg && this._frameImg.complete) {
      ctx.drawImage(this._frameImg, 0, 0, w, h);
    }
  };

  PosterPersonalizer.prototype._loadPlaceholderImage = function () {
    var url = this.config.placeholderImage;
    if (!url) return;
    var self = this;
    var img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = function () { self._placeholderImg = img; self._drawCanvas(); };
    img.src = url;
  };

  PosterPersonalizer.prototype._drawPlaceholder = function (areaX, areaY, areaW, areaH) {
    var ctx = this.ctx;

    // Si hay imagen de placeholder, se dibuja dentro del hueco y listo
    var pimg = this._placeholderImg;
    if (pimg && pimg.complete && pimg.naturalWidth > 0) {
      ctx.save();
      ctx.beginPath();
      ctx.rect(areaX, areaY, areaW, areaH);
      ctx.clip();
      ctx.drawImage(pimg, areaX, areaY, areaW, areaH);
      ctx.restore();
      return;
    }

    var bg = this.config.placeholderBg || '#ECEAE6';
    var fg = this.config.placeholderColor || '#9a9a9a';
    var text = this.config.placeholderText || 'TU DISEÑO IRÁ AQUÍ';

    ctx.save();
    // Fondo del hueco
    ctx.fillStyle = bg;
    ctx.fillRect(areaX, areaY, areaW, areaH);

    // Texto centrado (se ajusta para caber en el ancho del hueco)
    ctx.fillStyle = fg;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    var fontSize = Math.max(14, Math.round(areaW * 0.09));
    var words = String(text).split(/\s+/);
    // Auto-shrink simple: bajar hasta que la palabra más larga entre
    for (var i = 0; i < 40; i++) {
      ctx.font = '700 ' + fontSize + 'px sans-serif';
      var maxWordW = 0;
      words.forEach(function (wd) { maxWordW = Math.max(maxWordW, ctx.measureText(wd).width); });
      if (maxWordW <= areaW * 0.82 || fontSize <= 12) break;
      fontSize -= 1;
    }
    ctx.font = '700 ' + fontSize + 'px sans-serif';

    // Envolver en líneas según el ancho
    var lines = [];
    var cur = '';
    words.forEach(function (wd) {
      var test = cur ? (cur + ' ' + wd) : wd;
      if (ctx.measureText(test).width > areaW * 0.82 && cur) { lines.push(cur); cur = wd; }
      else { cur = test; }
    });
    if (cur) lines.push(cur);

    var lineH = fontSize * 1.25;
    var startY = areaY + areaH / 2 - (lines.length - 1) * lineH / 2;
    var cxText = areaX + areaW / 2;
    lines.forEach(function (ln, idx) {
      ctx.fillText(ln, cxText, startY + idx * lineH);
    });
    ctx.restore();
  };

  // ----------------------------------------------------------
  // SUBIR FOTO (selección de archivo)
  // ----------------------------------------------------------
  PosterPersonalizer.prototype._handleFile = function (file) {
    if (!file) return;
    if (ALLOWED_TYPES.indexOf(file.type) === -1) {
      this._showMsg('error', 'Formato no soportado. Usá JPEG, PNG o WebP.');
      return;
    }
    if (file.size > MAX_BYTES) {
      this._showMsg('error', 'La imagen es muy pesada (máximo 24 MB). Probá con una un poco más liviana.');
      return;
    }

    var self = this;
    this.state.photoFile = file;
    this.state.originalUrl = null;
    this.state.previewUrl = null;
    this.state.uploadDone = false;
    this._ref = this._genRef(); // código único para este póster
    this.state.transform = { x: 0, y: 0, scale: 1, rotation: 0 };

    if (this.state.photoUrl) URL.revokeObjectURL(this.state.photoUrl);
    this.state.photoUrl = URL.createObjectURL(file);

    var img = new Image();
    img.onload = function () {
      self.state._photoImg = img;
      self._checkResolution(img.naturalWidth, img.naturalHeight);
      self._drawCanvas();
      self._updateControlsVisibility();
      self._syncSliders();
      // Subir el ORIGINAL en alta resolución (con modal de progreso)
      self._uploadOriginal(file);
    };
    img.src = this.state.photoUrl;

    this._showMsg('', '');
    this._injectHiddenInputs();
    this._updateStatus();
  };

  // Aviso amable de baja resolución (NO bloquea la compra)
  PosterPersonalizer.prototype._checkResolution = function (pw, ph) {
    var note = this.el.querySelector('[data-pzp-reso-note]');
    if (!note) return;
    var minShort = parseInt(this.config.minResolutionPx, 10) || 1500;
    var short = Math.min(pw, ph);
    if (short < minShort) {
      var msg = this.config.lowResMessage ||
        'Tu foto funciona, pero para que el poster se vea con la mejor nitidez te recomendamos una imagen un poco más grande.';
      note.textContent = msg;
      note.style.display = '';
    } else {
      note.style.display = 'none';
    }
  };

  // ----------------------------------------------------------
  // SUBIDA DEL ORIGINAL — binario crudo + modal de progreso
  // ----------------------------------------------------------
  PosterPersonalizer.prototype._uploadOriginal = function (file) {
    var self = this;
    var url = this.el.dataset.pzdPosterUploadUrl;
    var key = this.el.dataset.pzdUploadKey;
    if (!url || !key) return;

    this.state.uploading = true;
    this.state.uploadDone = false;
    this._injectHiddenInputs();
    this._updateStatus();
    this._openUploadModal(file.size);

    var productId = this.el.dataset.pzdVariantId || '';
    var xhr = new XMLHttpRequest();
    xhr.open('POST', url + '?kind=original&productId=' + encodeURIComponent(productId), true);
    xhr.setRequestHeader('X-Upload-Key', key);
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');

    this._upStart = Date.now();
    xhr.upload.onprogress = function (e) {
      if (e.lengthComputable) self._updateUploadModal(e.loaded, e.total);
    };

    xhr.onload = function () {
      self.state.uploading = false;
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          var res = JSON.parse(xhr.responseText);
          if (res.url) {
            self.state.originalUrl = res.url;
            self.state.uploadDone = true;
            self._finishUploadModal(true);
            self._setPhotoStatus('', '');
            self._schedulePreviewUpload();
          } else {
            self._finishUploadModal(false, 'No pudimos guardar tu imagen. Intentá de nuevo.');
            self._setPhotoStatus('error', 'Error al subir. Tocá "Cambiar foto" y volvé a intentar.');
          }
        } catch (err) {
          self._finishUploadModal(false, 'No pudimos guardar tu imagen. Intentá de nuevo.');
          self._setPhotoStatus('error', 'Error al subir. Tocá "Cambiar foto" y volvé a intentar.');
        }
      } else {
        var emsg = 'No pudimos subir tu imagen. Intentá de nuevo.';
        try { var er = JSON.parse(xhr.responseText); if (er.error) emsg = er.error; } catch (e2) {}
        self._finishUploadModal(false, emsg);
        self._setPhotoStatus('error', emsg);
      }
      self._injectHiddenInputs();
      self._updateStatus();
    };

    xhr.onerror = function () {
      self.state.uploading = false;
      self._finishUploadModal(false, 'Se cortó la conexión. Revisá tu internet e intentá de nuevo.');
      self._setPhotoStatus('error', 'Se cortó la conexión. Revisá tu internet y tocá "Cambiar foto".');
      self._injectHiddenInputs();
      self._updateStatus();
    };

    xhr.send(file);
  };

  // ----------------------------------------------------------
  // MODAL DE PROGRESO — barra + velocidad + tiempo estimado
  // ----------------------------------------------------------
  PosterPersonalizer.prototype._openUploadModal = function (totalBytes) {
    var modal = this.el.querySelector('[data-pzp-upmodal]');
    if (!modal) return;
    this._portalModal(modal, 'uploadModal');
    var fill = modal.querySelector('[data-pzp-upbar]');
    var pct = modal.querySelector('[data-pzp-uppct]');
    var msg = modal.querySelector('[data-pzp-upmsg]');
    var eta = modal.querySelector('[data-pzp-upeta]');
    if (fill) fill.style.width = '0%';
    if (pct) pct.textContent = '0%';
    if (msg) msg.textContent = 'Preparando tu imagen...';
    if (eta) eta.textContent = '';
    modal.classList.remove('is-error', 'is-done');
    modal.style.display = 'flex';
  };

  PosterPersonalizer.prototype._updateUploadModal = function (loaded, total) {
    var modal = this.uploadModal || this.el.querySelector('[data-pzp-upmodal]');
    if (!modal) return;
    var ratio = total > 0 ? (loaded / total) : 0;
    var percent = Math.min(100, Math.round(ratio * 100));

    var elapsed = (Date.now() - this._upStart) / 1000; // s
    var speed = elapsed > 0 ? (loaded / elapsed) : 0;   // bytes/s
    var remainBytes = Math.max(0, total - loaded);
    var remainSec = speed > 0 ? Math.ceil(remainBytes / speed) : null;

    var fill = modal.querySelector('[data-pzp-upbar]');
    var pct = modal.querySelector('[data-pzp-uppct]');
    var msg = modal.querySelector('[data-pzp-upmsg]');
    var eta = modal.querySelector('[data-pzp-upeta]');

    if (fill) fill.style.width = percent + '%';
    if (pct) pct.textContent = percent + '%';

    // Mensaje dinámico según la velocidad
    if (msg) {
      var mbps = speed / (1024 * 1024);
      var text;
      if (percent >= 99) text = 'Casi listo, dando los últimos toques...';
      else if (mbps >= 3) text = '¡Tu internet vuela! Esto va a estar enseguida.';
      else if (mbps >= 0.8) text = 'Tu conexión va a buen ritmo, ya casi.';
      else text = 'Tu conexión va tranquila, en unos segundos termina.';
      msg.textContent = text;
    }

    if (eta) {
      if (remainSec === null || percent >= 99) {
        eta.textContent = '';
      } else if (remainSec <= 1) {
        eta.textContent = 'Tiempo restante: menos de 1 segundo';
      } else {
        eta.textContent = 'Tiempo restante: ~' + remainSec + ' segundos';
      }
    }
  };

  PosterPersonalizer.prototype._finishUploadModal = function (ok, errMsg) {
    var modal = this.uploadModal || this.el.querySelector('[data-pzp-upmodal]');
    if (!modal) return;
    var fill = modal.querySelector('[data-pzp-upbar]');
    var pct = modal.querySelector('[data-pzp-uppct]');
    var msg = modal.querySelector('[data-pzp-upmsg]');
    var eta = modal.querySelector('[data-pzp-upeta]');

    if (ok) {
      modal.classList.add('is-done');
      if (fill) fill.style.width = '100%';
      if (pct) pct.textContent = '100%';
      if (msg) msg.textContent = '¡Listo! Tu imagen se cargó correctamente.';
      if (eta) eta.textContent = '';
      // Cierre automático
      setTimeout(function () { modal.style.display = 'none'; }, 900);
    } else {
      modal.classList.add('is-error');
      if (msg) msg.textContent = errMsg || 'Algo salió mal. Intentá de nuevo.';
      if (eta) eta.textContent = '';
      var self = this;
      setTimeout(function () { modal.style.display = 'none'; self._showMsg('error', errMsg || 'No se pudo subir la imagen.'); }, 1800);
    }
  };

  // ----------------------------------------------------------
  // VISTA PREVIA COMPUESTA (cómo quedó encuadrada)
  // ----------------------------------------------------------
  PosterPersonalizer.prototype._schedulePreviewUpload = function () {
    var self = this;
    if (this._previewTimer) clearTimeout(this._previewTimer);
    this._previewTimer = setTimeout(function () { self._uploadPreview(); }, 1200);
  };

  PosterPersonalizer.prototype._uploadPreview = function () {
    var self = this;
    if (!this.canvas || !this.state._photoImg) return;
    var url = this.el.dataset.pzdPosterUploadUrl;
    var key = this.el.dataset.pzdUploadKey;
    if (!url || !key) return;

    this.canvas.toBlob(function (blob) {
      if (!blob) return;
      var productId = self.el.dataset.pzdVariantId || '';
      var xhr = new XMLHttpRequest();
      xhr.open('POST', url + '?kind=preview&productId=' + encodeURIComponent(productId), true);
      xhr.setRequestHeader('X-Upload-Key', key);
      xhr.setRequestHeader('Content-Type', 'image/jpeg');
      xhr.onload = function () {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            var res = JSON.parse(xhr.responseText);
            if (res.url) { self.state.previewUrl = res.url; self._injectHiddenInputs(); }
          } catch (e) {}
        }
      };
      xhr.send(blob);
    }, 'image/jpeg', 0.9);
  };

  // ----------------------------------------------------------
  // CONTROLES (sliders) + drag
  // ----------------------------------------------------------
  PosterPersonalizer.prototype._updateControlsVisibility = function () {
    var hasPhoto = !!(this.state._photoImg && this.state._photoImg.complete);
    var controls = this.el.querySelector('[data-pzp-controls]');
    var empty = this.el.querySelector('[data-pzp-empty]');
    var hint = this.el.querySelector('[data-pzp-context-hint]');
    if (controls) controls.style.display = hasPhoto ? '' : 'none';
    if (empty) empty.style.display = hasPhoto ? 'none' : '';
    // Hint contextual (Usá los controles...) solo cuando hay foto cargada
    if (hint) hint.classList.toggle('is-visible', hasPhoto);
  };

  PosterPersonalizer.prototype._syncSliders = function () {
    var t = this.state.transform;
    var setVal = function (sel, val) {
      var s = this.el.querySelector(sel);
      if (s) s.value = val;
    }.bind(this);
    setVal('[data-pzp-pos][data-pzp-axis="x"]', Math.round((t.x || 0) * 100));
    setVal('[data-pzp-pos][data-pzp-axis="y"]', Math.round(-(t.y || 0) * 100));
    setVal('[data-pzp-zoom]', Math.round((t.scale || 1) * 150));
  };

  PosterPersonalizer.prototype._bindEvents = function () {
    var self = this;

    // Input de archivo
    var fileInput = this.el.querySelector('[data-pzp-file]');
    var triggers = this.el.querySelectorAll('[data-pzp-upload-trigger], [data-pzp-change]');
    triggers.forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        if (fileInput) fileInput.click();
      });
    });
    if (fileInput) {
      fileInput.addEventListener('change', function (e) {
        var f = e.target.files && e.target.files[0];
        if (f) self._handleFile(f);
        e.target.value = ''; // permitir re-subir el mismo archivo
      });
    }

    // Click en el canvas cuando está vacío → abrir selector de archivo
    if (this.canvas) {
      this.canvas.addEventListener('click', function () {
        if (!self.state._photoImg && fileInput) fileInput.click();
      });
    }

    // Guard táctil PRIMERO (se registra antes que los listeners de transform,
    // así corre antes y, si el toque cayó en el track, bloquea el movimiento).
    this._setupSliderTouchGuard();

    // Sliders de posición
    this.el.querySelectorAll('[data-pzp-pos]').forEach(function (slider) {
      slider.addEventListener('input', function () {
        if (self._isLocked(slider)) return; // toque en el track → no mover
        var axis = slider.dataset.pzpAxis;
        var v = parseInt(slider.value, 10) / 100;
        if (axis === 'x') self.state.transform.x = v;
        else if (axis === 'y') self.state.transform.y = -v; // invertido para que arriba sea arriba
        self._drawCanvas();
        self._schedulePreviewUpload();
      });
    });

    // Zoom
    var zoom = this.el.querySelector('[data-pzp-zoom]');
    if (zoom) zoom.addEventListener('input', function () {
      if (self._isLocked(zoom)) return;
      // value 150 = escala 1 (slider centrado), igual que dual-v2
      self.state.transform.scale = parseInt(zoom.value, 10) / 150;
      self._drawCanvas();
      self._schedulePreviewUpload();
    });

    // Reset
    var reset = this.el.querySelector('[data-pzp-reset]');
    if (reset) reset.addEventListener('click', function (e) {
      e.preventDefault();
      self.state.transform = { x: 0, y: 0, scale: 1, rotation: 0 };
      self._syncSliders();
      self._drawCanvas();
      self._schedulePreviewUpload();
    });

    // Drag SOLO con mouse (desktop). En celular NO hay drag para que el
    // scroll de la página nunca se trabe — el cliente usa los sliders.
    this._bindCanvasMouseDrag();

    // Vista previa modal
    var previewBtn = this.el.querySelector('[data-pzp-preview-open]');
    if (previewBtn) previewBtn.addEventListener('click', function (e) { e.preventDefault(); self._openPreviewModal(); });
  };

  // Evita el "salto" del slider al tocar el track en móvil: si el toque
  // cae lejos del punto, fija el valor actual hasta que se levanta el dedo.
  PosterPersonalizer.prototype._setupSliderTouchGuard = function () {
    var self = this;
    var locked = this._sliderLocked = new WeakMap();
    this.el.querySelectorAll('.pzp__slider-input').forEach(function (slider) {
      slider.addEventListener('touchstart', function (e) {
        var rect = slider.getBoundingClientRect();
        var min = parseFloat(slider.min);
        var max = parseFloat(slider.max);
        var val = parseFloat(slider.value);
        var ratio = (val - min) / (max - min);
        var thumbX = rect.left + ratio * rect.width;
        var clientX = e.touches[0].clientX;
        // Si el dedo NO cayó sobre el punto → bloquear: ni el punto ni la imagen se mueven
        if (Math.abs(clientX - thumbX) > 18) {
          locked.set(slider, val);
          var unlock = function () {
            locked.delete(slider);
            slider.value = val;
            document.removeEventListener('touchend', unlock);
            document.removeEventListener('touchcancel', unlock);
          };
          document.addEventListener('touchend', unlock);
          document.addEventListener('touchcancel', unlock);
        }
      }, { passive: true });
      // Este input handler corre PRIMERO (se registra antes que los de transform):
      // si está bloqueado, restaura el valor para que los demás lean el valor viejo.
      slider.addEventListener('input', function () {
        if (locked.has(slider)) slider.value = locked.get(slider);
      });
    });
  };

  // ¿Está bloqueado este slider por el guard táctil? (toque en el track, no en el punto)
  PosterPersonalizer.prototype._isLocked = function (slider) {
    return this._sliderLocked && this._sliderLocked.has(slider);
  };

  PosterPersonalizer.prototype._bindCanvasMouseDrag = function () {
    var self = this;
    if (!this.canvas) return;
    var last = null;
    var onDown = function (e) {
      if (!self.state._photoImg) return;
      self._dragging = true;
      last = { x: e.clientX, y: e.clientY };
    };
    var onMove = function (e) {
      if (!self._dragging || !last) return;
      var rect = self.canvas.getBoundingClientRect();
      self.state.transform.x += (e.clientX - last.x) / rect.width;
      self.state.transform.y += (e.clientY - last.y) / rect.height;
      last = { x: e.clientX, y: e.clientY };
      self._syncSliders();
      self._drawCanvas();
    };
    var onUp = function () {
      if (self._dragging) { self._dragging = false; last = null; self._schedulePreviewUpload(); }
    };
    this.canvas.addEventListener('mousedown', onDown);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // ----------------------------------------------------------
  // PORTAL AL BODY (para que position:fixed funcione en cualquier tema)
  // ----------------------------------------------------------
  PosterPersonalizer.prototype._portalModal = function (modal, refName) {
    if (modal._pzpPortaled) { this[refName] = modal; return; }
    // Copiar variables CSS --pzp-* al modal antes de moverlo
    var cs = getComputedStyle(this.el);
    ['--pzp-accent', '--pzp-label-size'].forEach(function (v) {
      var val = cs.getPropertyValue(v);
      if (val) modal.style.setProperty(v, val.trim());
    });
    document.body.appendChild(modal);
    modal._pzpPortaled = true;
    this[refName] = modal;
  };

  PosterPersonalizer.prototype._openPreviewModal = function () {
    var modal = this.el.querySelector('[data-pzp-preview-modal]') || this.previewModal;
    if (!modal) return;
    this._portalModal(modal, 'previewModal');
    var pImg = modal.querySelector('[data-pzp-preview-img]');
    if (pImg && this.canvas) pImg.src = __pzSobreBlanco(this.canvas).toDataURL('image/jpeg', 0.9);
    modal.style.display = 'flex';
    var self = this;
    if (!modal._pzpCloseBound) {
      modal._pzpCloseBound = true;
      document.addEventListener('click', function (e) {
        if (modal.style.display === 'none') return;
        if (e.target.closest && e.target.closest('[data-pzp-preview-close]') && modal.contains(e.target)) {
          modal.style.display = 'none';
        }
      });
    }
  };

  // ----------------------------------------------------------
  // PROPIEDADES DEL PEDIDO
  // ----------------------------------------------------------
  // Código corto único por póster (para distinguir varios en el carrito)
  PosterPersonalizer.prototype._genRef = function () {
    var chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    var s = '';
    for (var i = 0; i < 4; i++) s += chars.charAt(Math.floor(Math.random() * chars.length));
    return s;
  };

  PosterPersonalizer.prototype._buildProperties = function () {
    var props = {};
    var labels = this.config.cartLabels || {};
    var lVisible = labels.visible || 'Imagen personalizada';
    var lVisibleValue = labels.visibleValue || 'Cargada correctamente';
    var lImage = labels.image || 'Imagen del cliente';
    var lPreview = labels.preview || 'Vista Previa';
    if (this.state.originalUrl) {
      // VISIBLE: confirma en el carrito + código corto para diferenciar varios pósters
      var ref = this._ref || '';
      props[lVisible] = lVisibleValue + (ref ? ' (#' + ref + ')' : '');
      // OCULTAS (empiezan con "_") — solo para vos en el admin del pedido.
      // El script del carrito usa _ref + _Vista Previa para inyectar el botón "Ver".
      if (ref) props['_ref'] = ref;
      props['_' + lImage] = this.state.originalUrl;
      if (this.state.previewUrl) props['_' + lPreview] = this.state.previewUrl;
    }
    return props;
  };

  PosterPersonalizer.prototype._injectHiddenInputs = function () {
    if (!this.productForm) return;
    var formId = this.el.dataset.pzdFormId || '';
    var props = this._buildProperties();
    // Limpiar inputs previos de este personalizador
    var prev = this.productForm.querySelectorAll('input[data-pzp-prop]');
    prev.forEach(function (n) { n.parentNode.removeChild(n); });
    var self = this;
    Object.keys(props).forEach(function (key) {
      var input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'properties[' + key + ']';
      input.value = props[key];
      input.setAttribute('data-pzp-prop', '');
      if (formId) input.setAttribute('form', formId);
      self.productForm.appendChild(input);
    });
  };

  // ----------------------------------------------------------
  // FORM + INTERCEPTORES
  // ----------------------------------------------------------
  PosterPersonalizer.prototype._findProductForm = function () {
    var formId = this.el.dataset.pzdFormId;
    if (formId) {
      var byId = document.getElementById(formId);
      if (byId) return byId;
    }
    return document.querySelector('form[action*="/cart/add"]');
  };

  PosterPersonalizer.prototype._validate = function () {
    if (!this.state.photoFile || !this.state._photoImg) {
      this._showMsg('error', 'Subí tu foto antes de agregar al carrito.');
      return false;
    }
    if (this.state.uploading) {
      this._showMsg('error', 'Esperá unos segundos a que termine de subir tu imagen.');
      return false;
    }
    if (!this.state.originalUrl) {
      this._showMsg('error', 'Tu imagen aún no terminó de subir. Probá de nuevo en un momento.');
      return false;
    }
    return true;
  };

  PosterPersonalizer.prototype._interceptFormSubmit = function () {
    var self = this;
    this.productForm.addEventListener('submit', function (e) {
      self._injectHiddenInputs();
      if (!self._validate()) {
        e.preventDefault();
        e.stopPropagation();
      }
    }, true);
  };

  PosterPersonalizer.prototype._interceptFetchAddToCart = function () {
    var self = this;
    var originalFetch = window.fetch;
    window.fetch = function (input, options) {
      var url = (typeof input === 'string') ? input : (input && input.url) || '';
      if (url.indexOf('/cart/add') !== -1) {
        self._injectHiddenInputs();
        if (!self._validate()) {
          return Promise.reject(new Error('Personalización incompleta'));
        }
        var pzProps = self._buildProperties();
        if (options && options.body) {
          if (options.body instanceof FormData) {
            Object.keys(pzProps).forEach(function (key) { options.body.set('properties[' + key + ']', pzProps[key]); });
          } else if (typeof options.body === 'string') {
            try {
              var json = JSON.parse(options.body);
              json.properties = Object.assign({}, json.properties || {}, pzProps);
              options.body = JSON.stringify(json);
            } catch (err) {
              var params = new URLSearchParams(options.body);
              Object.keys(pzProps).forEach(function (key) { params.set('properties[' + key + ']', pzProps[key]); });
              options.body = params.toString();
            }
          }
        }
      }
      return originalFetch.apply(this, arguments);
    };
  };

  PosterPersonalizer.prototype._interceptXHRAddToCart = function () {
    var self = this;
    var originalOpen = XMLHttpRequest.prototype.open;
    var originalSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) {
      this._pzpUrl = url;
      return originalOpen.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function (body) {
      if (this._pzpUrl && this._pzpUrl.indexOf('/cart/add') !== -1) {
        self._injectHiddenInputs();
        if (!self._validate()) { return; }
        var pzProps = self._buildProperties();
        if (body instanceof FormData) {
          Object.keys(pzProps).forEach(function (key) { body.set('properties[' + key + ']', pzProps[key]); });
        } else if (typeof body === 'string') {
          try {
            var json = JSON.parse(body);
            json.properties = Object.assign({}, json.properties || {}, pzProps);
            arguments[0] = JSON.stringify(json);
            return originalSend.call(this, JSON.stringify(json));
          } catch (err) {
            var params = new URLSearchParams(body);
            Object.keys(pzProps).forEach(function (key) { params.set('properties[' + key + ']', pzProps[key]); });
            return originalSend.call(this, params.toString());
          }
        }
      }
      return originalSend.apply(this, arguments);
    };
  };

  PosterPersonalizer.prototype._interceptReelAsItButton = function () {
    var self = this;
    document.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('._rsi-buy-now-button');
      if (!btn) return;
      self._injectHiddenInputs();
      if (!self._validate()) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    }, true);
  };

  // ----------------------------------------------------------
  // MENSAJES
  // ----------------------------------------------------------
  PosterPersonalizer.prototype._showMsg = function (type, text) {
    var msg = this.el.querySelector('[data-pzp-msg]');
    if (!msg) return;
    msg.textContent = text || '';
    msg.className = 'pzp__msg' + (text ? ' is-visible is-' + type : '');
  };

  // ----------------------------------------------------------
  // INIT
  // ----------------------------------------------------------
  function initAll() {
    var nodes = document.querySelectorAll('[data-product-personalizer-poster]');
    nodes.forEach(function (el) {
      if (el._pzpInstance) return;
      try { el._pzpInstance = new PosterPersonalizer(el); }
      catch (e) { console.error('[PZ-POSTER] init error:', e); }
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();
})();
